// Remote catalog — code-defined and MF-exposed (via ./remoteRegistry), not
// runtime appsettings: nothing here is secret (it all reaches the browser),
// and a reviewed, type-checked array beats a fetched blob while the contract
// is evolving. If per-environment URLs ever require build-once-promote-many,
// swap the SOURCE of configureRemotes() — callers don't change.
//
// Expose names are NOT configurable: every routes-mode remote publishes
// exactly ./routes and ./i18nMessages (see remote-registry.ts). That is why
// there is no `routesExpose` field.
export interface RemoteConfig {
  id: string;
  remoteName: string;
  alias?: string;
  entry: string;
  entryType?: "module" | "global";
  routePrefixes: string[];
  preload?: boolean;
  requiresAuthByDefault?: boolean;
  /**
   * OneID permission name required to enter this remote's routes (checked as
   * `can("manage", <name>)` against the rbac ability). Merged into each
   * registered route's meta unless the route sets its own.
   */
  requiresPermission?: string;
  /**
   * "legacy": an AppMount remote (own Vue app + bootloader, MerchantApp
   * contract). The host mounts it through features/legacy/LegacyRemoteMount
   * under a catch-all route per prefix and shares its router through the
   * legacy router bridge. Default: a routes-mode (Contract v2) remote.
   */
  mode?: "legacy";
  /**
   * Legacy only: extra arguments passed to the remote's mount() on its page
   * routes (MerchantApp's `mountOptions`, e.g. `displayProperties`).
   */
  mountOptions?: Record<string, unknown>;
}

export const defaultRemotes: RemoteConfig[] = [
  {
    id: "customersUi",
    remoteName: "shipbob_customers_ui",
    alias: "shipbob_customers_ui",
    entry: "https://customersui-dev.shipbob.dev/mf-manifest.json",
    entryType: "module",
    routePrefixes: ["/Customers"],
    requiresAuthByDefault: true,
    mode: "legacy",
  },
  {
    id: "userManagementUi",
    remoteName: "shipbob_user_management_ui",
    alias: "shipbob_user_management_ui",
    entry: "https://usermanagementui-dev.shipbob.dev/mf-manifest.json",
    entryType: "module",
    routePrefixes: ["/user-management"],
    requiresAuthByDefault: true,
    mode: "legacy",
    // What MerchantApp passes on its /user-management route.
    mountOptions: {
      displayProperties: { hideHomeOrganization: true, hideFilters: true, emailUsersOnly: true },
    },
  },
  {
    id: "reportingUi",
    remoteName: "shipbob_reporting_ui",
    alias: "shipbob_reporting_ui",
    entry: "https://reportingui-dev.shipbob.dev/mf-manifest.json",
    entryType: "module",
    // MerchantApp's `/analytics*` route (router/analyticsUI.js); the remote's own
    // records are rooted at its MFE_ROOT_ROUTE, `/analytics`.
    routePrefixes: ["/analytics"],
    requiresAuthByDefault: true,
    mode: "legacy",
  },
];

/**
 * Applies per-environment entry-URL overrides (appsettings `remoteEntries`,
 * keyed by remote id) over a code-defined catalog. Structure stays code;
 * URLs stay ops-editable without a rebuild.
 */
export function applyRemoteEntryOverrides(
  remotes: RemoteConfig[],
  remoteEntries: Record<string, string> | undefined,
): RemoteConfig[] {
  if (!remoteEntries) return remotes;
  return remotes.map((remote) => {
    const entry = remoteEntries[remote.id];
    return entry ? { ...remote, entry } : remote;
  });
}
